import { PrefabProvider } from "@prefab-cloud/prefab-cloud-react";
import { MotionConfig } from "framer-motion";
import * as React from "react";
import * as Analytics from "~/contexts/analyticsContext";
import * as Authentication from "~/contexts/authenticationContext";
import * as Dialog from "~/contexts/dialogContext";
import { MobileMenuProvider } from "~/contexts/mobileMenuContext";
import * as Notifications from "~/contexts/notificationsContext";
import * as Portal from "~/contexts/portalContext";
import * as ReactRouterStubs from "~/contexts/reactRouterStubsContext";
import * as RequestHeaders from "~/contexts/requestHeadersContext";
import * as Search from "~/contexts/searchContext";
import * as TrackedExperiments from "~/contexts/trackedExperiments";
import * as Prefab from "~/domain/prefab";

export type Props = {
  children?: React.ReactNode;
  prefabApiKey?: string;
  analytics: Analytics.AnalyticsContextProps;
  authentication: Authentication.AuthenticationContextProps;
  notifications: Notifications.NotificationsContext;
  search: Search.SearchContextProps;
  dialog: Dialog.DialogContextProps;
  portal: Portal.PortalContextProps;
  requestHeaders: RequestHeaders.RequestHeadersContextProps;
  reactRouterStubs: ReactRouterStubs.ReactRouterStubs;
};

export const MarketplaceContexts: React.FC<Props> = (props) => {
  const [trackedExperimentsState, trackedExperimentsDispatch] =
    React.useReducer(TrackedExperiments.reducer, TrackedExperiments.init());

  const prefabContext = React.useMemo(
    () =>
      Prefab.makeContext({
        anonymousId: props.analytics.anonymousId ?? undefined,
        identity: props.authentication.identity ?? undefined,
      }),
    [props.analytics.anonymousId, props.authentication.identity],
  );

  return (
    <TrackedExperiments.DispatchContext.Provider
      value={trackedExperimentsDispatch}
    >
      <TrackedExperiments.StateContext.Provider value={trackedExperimentsState}>
        <Analytics.AnalyticsContext.Provider value={props.analytics}>
          <Authentication.AuthenticationContext.Provider
            value={props.authentication}
          >
            <PrefabProvider
              apiKey={props.prefabApiKey ?? "not-set"}
              contextAttributes={prefabContext}
              collectEvaluationSummaries={false}
            >
              <Notifications.NotificationsContext.Provider
                value={props.notifications}
              >
                <MotionConfig reducedMotion="user">
                  <Search.SearchContext.Provider value={props.search}>
                    <Dialog.DialogContext.Provider value={props.dialog}>
                      <Portal.PortalContext.Provider value={props.portal}>
                        <RequestHeaders.RequestHeadersContext.Provider
                          value={props.requestHeaders}
                        >
                          <ReactRouterStubs.ReactRouterStubsContext.Provider
                            value={props.reactRouterStubs}
                          >
                            <MobileMenuProvider>
                              {props.children}
                            </MobileMenuProvider>
                          </ReactRouterStubs.ReactRouterStubsContext.Provider>
                        </RequestHeaders.RequestHeadersContext.Provider>
                      </Portal.PortalContext.Provider>
                    </Dialog.DialogContext.Provider>
                  </Search.SearchContext.Provider>
                </MotionConfig>
              </Notifications.NotificationsContext.Provider>
            </PrefabProvider>
          </Authentication.AuthenticationContext.Provider>
        </Analytics.AnalyticsContext.Provider>
      </TrackedExperiments.StateContext.Provider>
    </TrackedExperiments.DispatchContext.Provider>
  );
};
